/**
 * SuperAgentChat Component
 *
 * Main component for the Super Agent system interface.
 * Handles user input, API communication, and displays agent execution status.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import superAgentService from '../../services/api/superAgentService';
import { useSuperAgentPolling } from '../../hooks/useSuperAgentPolling';
import {
  SuperAgentAsyncResponse,
  SuperAgentStatusResponse,
  AgentResult,
  ExecutionStatus,
  ProcessingStep,
  SpecialistAgentType
} from '../../types/superAgent';
import AgentExecutionPanel from './AgentExecutionPanel';
import AgentResultCard from './AgentResultCard';
import FinalResponseCard from './FinalResponseCard';

interface SuperAgentChatProps {
  userId: number;
  onError?: (error: string) => void;
}

const SuperAgentChat: React.FC<SuperAgentChatProps> = ({ userId, onError }) => {
  // State management
  const [userInput, setUserInput] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [executionId, setExecutionId] = useState<number | null>(null);
  const [selectedAgents, setSelectedAgents] = useState<SpecialistAgentType[]>([]);
  const [agentResults, setAgentResults] = useState<AgentResult[]>([]);
  const [finalResponse, setFinalResponse] = useState<string>('');
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus | null>(null);
  const [progress, setProgress] = useState<SuperAgentStatusResponse['progress'] | null>(null);
  const [currentStep, setCurrentStep] = useState<ProcessingStep | null>(null);
  const [stepDetail, setStepDetail] = useState<string>('');
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const startTimeRef = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Use polling hook
  const {
    statusData,
    isPolling,
    error: pollingError,
    startPolling,
    stopPolling,
    clearError: clearPollingError
  } = useSuperAgentPolling();

  /**
   * Handle form submission (Async version)
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userInput.trim()) {
      return;
    }

    setIsProcessing(true);
    setExecutionStatus('pending');
    setAgentResults([]);
    setFinalResponse('');
    setSelectedAgents([]);
    setProgress(null);
    setCurrentStep(null);
    setStepDetail('');
    setElapsedTime(0);
    startTimeRef.current = Date.now();
    clearPollingError();

    // Start elapsed time counter
    timerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    try {
      // Process the user's request (returns immediately with execution_id)
      const response: SuperAgentAsyncResponse = await superAgentService.processRequest({
        user_request: userInput.trim(),
        max_agents: 3
      });

      // Store execution ID and status
      setExecutionId(response.execution_id);
      setExecutionStatus(response.status);
      setIsProcessing(false);

      // Start polling for status updates
      startPolling(response.execution_id);

    } catch (error: any) {
      console.error('SuperAgentChat: Error processing request', error);
      const errorMessage = error.message || 'An error occurred while processing your request';
      if (onError) {
        onError(errorMessage);
      }
      setExecutionStatus('failed');
      setIsProcessing(false);
    }
  }, [userInput, onError, startPolling, clearPollingError]);

  /**
   * Handle polling status updates
   */
  useEffect(() => {
    if (statusData) {
      // Update execution status and step
      setExecutionStatus(statusData.execution_status);
      setProgress(statusData.progress);
      if (statusData.current_step) {
        setCurrentStep(statusData.current_step);
      }
      if (statusData.step_detail) {
        setStepDetail(statusData.step_detail);
      }

      // If completed or failed, update with final results
      if (statusData.execution_status === 'completed' || statusData.execution_status === 'failed') {
        // Stop elapsed timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        if (statusData.selected_agents) {
          // Handle both array formats (object array or string array) and filter out null values
          const agents = Array.isArray(statusData.selected_agents)
            ? statusData.selected_agents
                .map((agent) => {
                  // If it's a string (SpecialistAgentType), return as-is
                  if (typeof agent === 'string') {
                    return agent as SpecialistAgentType;
                  }
                  // If it's an object with agent_type, extract agent_type
                  else if (agent && typeof agent === 'object' && 'agent_type' in agent) {
                    return agent.agent_type;
                  }
                  // Filter out null/undefined
                  return null;
                })
                .filter((agent): agent is SpecialistAgentType => agent !== null)
            : [];

          setSelectedAgents(agents);
        }
        if (statusData.results) {
          setAgentResults(statusData.results);
        }
        if (statusData.final_response) {
          setFinalResponse(statusData.final_response);
        }
      }
    }
  }, [statusData]);

  /**
   * Handle polling errors
   */
  useEffect(() => {
    if (pollingError && onError) {
      onError(pollingError);
    }
  }, [pollingError, onError]);

  /**
   * Reset the form
   */
  const handleReset = useCallback(() => {
    stopPolling();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setUserInput('');
    setExecutionId(null);
    setSelectedAgents([]);
    setAgentResults([]);
    setFinalResponse('');
    setExecutionStatus(null);
    setProgress(null);
    setCurrentStep(null);
    setStepDetail('');
    setElapsedTime(0);
    startTimeRef.current = null;
    clearPollingError();
  }, [stopPolling, clearPollingError]);

  /**
   * Format elapsed time for display
   */
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}分${secs}秒`;
    }
    return `${secs}秒`;
  };

  /**
   * Get step label for display
   */
  const getStepLabel = (step: ProcessingStep | null): string => {
    switch (step) {
      case 'analyzing': return 'リクエスト分析中';
      case 'executing': return 'エージェント実行中';
      case 'aggregating': return '結果統合中';
      case 'completed': return '完了';
      case 'failed': return 'エラー';
      default: return '準備中';
    }
  };

  /**
   * Get step number (1-based) for progress indicator
   */
  const getStepNumber = (step: ProcessingStep | null): number => {
    switch (step) {
      case 'analyzing': return 1;
      case 'executing': return 2;
      case 'aggregating': return 3;
      case 'completed': return 4;
      default: return 0;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Input Form */}
      <div className="p-4 border-b border-gray-200">
        <form onSubmit={handleSubmit} className="flex flex-col space-y-3">
          <textarea
            data-tutorial="chat-input"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Super Agentに依頼したい内容を入力してください..."
            className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            disabled={isProcessing}
          />
          <div className="flex space-x-2">
            <button
              type="submit"
              data-tutorial="chat-send"
              disabled={isProcessing || isPolling || !userInput.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? '送信中...' : isPolling ? '処理中...' : 'Super Agentに送信'}
            </button>
            {(executionStatus === 'completed' || executionStatus === 'failed') && (
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                新しいリクエスト
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Results Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Step Progress Indicator */}
        {(isPolling || executionStatus === 'pending' || executionStatus === 'running') && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            {/* Step Stepper */}
            <div className="flex items-center justify-between mb-4">
              {['analyzing', 'executing', 'aggregating', 'completed'].map((step, index) => {
                const stepNum = index + 1;
                const currentNum = getStepNumber(currentStep);
                const isActive = currentNum === stepNum;
                const isDone = currentNum > stepNum;
                return (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          isDone
                            ? 'bg-green-500 text-white'
                            : isActive
                            ? 'bg-blue-600 text-white ring-4 ring-blue-200'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {isDone ? '\u2713' : stepNum}
                      </div>
                      <span className={`text-xs mt-1 ${isActive ? 'text-blue-700 font-semibold' : 'text-gray-500'}`}>
                        {getStepLabel(step as ProcessingStep)}
                      </span>
                    </div>
                    {index < 3 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${
                          isDone ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Current Status */}
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                <span className="text-blue-900 font-semibold">
                  {stepDetail || getStepLabel(currentStep)}
                </span>
              </div>
              <span className="text-blue-700 text-sm font-bold">
                {formatElapsedTime(elapsedTime)}
              </span>
            </div>

            {/* Agent Progress Bar */}
            {progress && progress.total > 0 && (
              <>
                <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <div className="text-sm text-blue-700 flex justify-between">
                  <span>
                    <span className="text-green-600 font-semibold">{progress.completed}</span> 完了 /
                    <span className="text-yellow-600 font-semibold ml-1">{progress.running}</span> 実行中 /
                    <span className="text-gray-600 ml-1">{progress.pending}</span> 待機中
                  </span>
                  <span className="font-bold">{progress.percentage}%</span>
                </div>
                {progress.failed > 0 && (
                  <div className="text-sm text-red-600 font-semibold mt-1">{progress.failed}件のエラー</div>
                )}
              </>
            )}
          </div>
        )}

        {/* Completion Banner */}
        {executionStatus === 'completed' && finalResponse && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-green-600 text-lg">{'\u2713'}</span>
              <span className="text-green-800 font-semibold">
                処理完了 ({formatElapsedTime(elapsedTime)})
              </span>
            </div>
            <span className="text-green-600 text-sm">
              {agentResults.filter(r => r.status === 'completed').length}/{agentResults.length} エージェント成功
            </span>
          </div>
        )}

        {/* Agent Execution Panel */}
        {selectedAgents.length > 0 && (
          <AgentExecutionPanel
            selectedAgents={selectedAgents}
            executionStatus={executionStatus || 'pending'}
            agentResults={agentResults}
          />
        )}

        {/* Individual Agent Results */}
        {agentResults.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900">Agent Results</h3>
            <div className="space-y-2">
              {agentResults.map((result) => (
                <AgentResultCard key={result.id} result={result} />
              ))}
            </div>
          </div>
        )}

        {/* Final Response */}
        {finalResponse && executionStatus === 'completed' && (
          <FinalResponseCard
            response={finalResponse}
            metadata={{
              total_agents: selectedAgents.length,
              successful_agents: agentResults.filter(r => r.status === 'completed').length,
              failed_agents: agentResults.filter(r => r.status === 'failed').length,
              success_rate: (agentResults.filter(r => r.status === 'completed').length / agentResults.length) * 100
            }}
          />
        )}

        {/* Error State - Execution Failed */}
        {executionStatus === 'failed' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-red-800 font-semibold mb-2">処理に失敗しました</h3>
            <p className="text-red-700">
              {stepDetail || 'Super Agentの処理中にエラーが発生しました。再度お試しください。'}
            </p>
          </div>
        )}

        {/* Error State - Polling Error */}
        {pollingError && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <h3 className="text-orange-800 font-semibold mb-2">接続エラー</h3>
            <p className="text-orange-700">{pollingError}</p>
            <button
              onClick={clearPollingError}
              className="mt-2 text-sm text-orange-600 hover:text-orange-800 underline"
            >
              閉じる
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAgentChat;
